/**
 * ImageCropModal
 *
 * A modal that lets the coach zoom, pan, and rotate their uploaded photo
 * so the face ends up nicely centred in the circular avatar frame.
 *
 * Uses react-easy-crop for the cropper UI and returns a new File from
 * cropImage.js when the user clicks "Save".
 */
import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import getCroppedImgFile from './cropImage';
import css from './ImageCropModal.css';

const ImageCropModal = props => {
  const {
    isOpen,
    imageSrc,
    fileName,
    onCancel,
    onConfirm,
    cropShape = 'round',
    aspect = 1,
    title = 'Adjust your profile photo',
    subtitle = 'Zoom and drag to centre your face',
    saveLabel = 'Save photo',
  } = props;

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setIsProcessing(true);
    try {
      const croppedFile = await getCroppedImgFile(
        imageSrc,
        croppedAreaPixels,
        rotation,
        fileName || 'profile.jpg'
      );
      onConfirm(croppedFile);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to crop image', err);
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  if (!isOpen || !imageSrc) return null;

  return (
    <div className={css.overlay} role="dialog" aria-modal="true" aria-labelledby="crop-modal-title">
      <div className={css.modal}>
        <div className={css.header}>
          <h3 id="crop-modal-title" className={css.title}>
            {title}
          </h3>
          <p className={css.subtitle}>{subtitle}</p>
        </div>

        <div className={css.cropperWrapper}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            cropShape={cropShape}
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            restrictPosition={false}
          />
        </div>

        <div className={css.controls}>
          <div className={css.sliderRow}>
            <label htmlFor="zoom-slider" className={css.sliderLabel}>
              Zoom
            </label>
            <input
              id="zoom-slider"
              type="range"
              min={1}
              max={4}
              step={0.01}
              value={zoom}
              onChange={e => setZoom(Number(e.target.value))}
              className={css.slider}
              aria-label="Zoom"
            />
          </div>

          <div className={css.sliderRow}>
            <label htmlFor="rotation-slider" className={css.sliderLabel}>
              Rotate
            </label>
            <input
              id="rotation-slider"
              type="range"
              min={0}
              max={360}
              step={1}
              value={rotation}
              onChange={e => setRotation(Number(e.target.value))}
              className={css.slider}
              aria-label="Rotate"
            />
          </div>

          <button type="button" className={css.resetButton} onClick={handleReset}>
            Reset
          </button>
        </div>

        <div className={css.actions}>
          <button
            type="button"
            className={css.cancelButton}
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            type="button"
            className={css.saveButton}
            onClick={handleSave}
            disabled={isProcessing || !croppedAreaPixels}
          >
            {isProcessing ? 'Saving...' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
